if (!customElements.get('media-vertical-scroll')) {
  customElements.define('media-vertical-scroll', class MediaVerticalScroll extends HTMLElement {
    constructor() {
      super();
      this.curr = 0;
      this.target = 0;
      this.raf = null;
      this.calcRaf = null;
      this.currentColCount = 0;
      this.lastWinWidth = window.innerWidth;
      this.lastWinHeight = window.innerHeight;
      this.isIntersecting = false;
      this.boundOnLoad = this.handleLoad.bind(this);
      this.boundOnResize = this.handleResize.bind(this);
      this.boundLazyReleaseCheck = this.checkLazyRelease.bind(this);
      this.resizeObserver = ('ResizeObserver' in window)
        ? new ResizeObserver(() => this.queueCalc())
        : null;
      this.lastContentHeightValue = '';
      this.lastSectionHeightValue = '';
      this.smoothedTarget = 0;
      this.lerpFactor = 0.5;

      this.parentShopifySection = this.closest('.shopify-section');
      this.preloadNextSectionImages();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.grid = this.querySelector('.media-vert-scroll__grid');
      if (!this.grid) return;

      this.items = Array.from(this.grid.querySelectorAll('.media'));
      this.parentSection = this.closest('.media-vert-scroll__section');

      this.baseSpeed = 0.5;
      this.ease = 0.15;
      this.scaleAmount = parseFloat(this.getAttribute('data-scale')) / 100;
      this.mobileScaleEnabled = true;

      if (document.readyState === 'complete') {
        this.queueCalc();
      } else {
        window.addEventListener('load', this.boundOnLoad, { once: true });
      }
      window.addEventListener('resize', this.boundOnResize);

      this.preloadOwnImages();
      this.calc();
      this.initObserver();
      this.hydrateUpcomingSections();
      this.scheduleLazyHold();
      setTimeout(() => this.grid.classList.remove('is-transparent'), 100);
    }

    preloadOwnImages() {
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    hydrateUpcomingSections() {
      customElements.get('lazy-section-template')?.hydrateUpcoming(this.parentShopifySection);
    }

    scheduleLazyHold() {
      const hold = () => {
        if (this.lazyHoldDone || this.lazyHeld) return;
        if (this.parentShopifySection.getBoundingClientRect().bottom <= 0) {
          this.lazyHoldDone = true;
          return;
        }
        this.holdLazyImages();
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(hold, { timeout: 1000 });
      } else {
        setTimeout(hold, 200);
      }
    }

    holdLazyImages() {
      if (this.lazyHeld || this.lazyHoldDone) return;
      this.lazyHeld = true;
      customElements.get('lazy-section-template')?.holdLazyAfter(this.parentShopifySection);
      window.addEventListener('scroll', this.boundLazyReleaseCheck, { passive: true });
    }

    releaseLazyImages() {
      if (!this.lazyHeld) return;
      this.lazyHeld = false;
      customElements.get('lazy-section-template')?.releaseLazyAfter(this.parentShopifySection);
      window.removeEventListener('scroll', this.boundLazyReleaseCheck);
      clearTimeout(this.lazyReleaseTimer);
    }

    checkLazyRelease() {
      clearTimeout(this.lazyReleaseTimer);
      this.lazyReleaseTimer = setTimeout(() => {
        if (!this.lazyHeld || this.isIntersecting) return;
        if (this.parentShopifySection.getBoundingClientRect().bottom <= 0) this.finishLazyHold();
      }, 150);
    }

    finishLazyHold() {
      this.lazyHoldDone = true;
      this.releaseLazyImages();
    }

    handleResize() {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      const isMobile = DeviceUtils.isMobileSmall;

      if (newWidth !== this.lastWinWidth || (!isMobile && newHeight !== this.lastWinHeight)) {
        this.lastWinWidth = newWidth;
        this.lastWinHeight = newHeight;
        this.queueCalc();
      }
    }

    handleLoad() {
      this.queueCalc();
    }

    queueCalc() {
      if (this.calcRaf) return;
      this.calcRaf = requestAnimationFrame(() => {
        this.calcRaf = null;
        this.calc();
      });
    }

    preloadNextSectionImages() {
      if (!this.parentShopifySection) return;

      const nextSection = this.parentShopifySection.nextElementSibling;
      if (!nextSection || !nextSection.classList.contains('shopify-section') || nextSection.dataset.preloaded) return;

      nextSection.dataset.preloaded = 'true';

      const lazySection = nextSection.querySelector('lazy-section-template');
      if (lazySection && !lazySection.hydrated && typeof lazySection.hydrate === 'function') {
        lazySection.hydrate('forced_preload_from_media_scroll');
        return;
      }

      nextSection.querySelectorAll('img').forEach(img => {
        if (img.loading === 'lazy') img.loading = 'eager';
      });
    }

    rebuildGrid(targetColCount) {
      const fragment = document.createDocumentFragment();
      this.columns = [];
      const isMobileSmall = typeof DeviceUtils !== 'undefined' && DeviceUtils.isMobileSmall;

      for (let i = 0; i < targetColCount; i++) {
        const col = document.createElement('div');
        col.className = 'media-vert-scroll__column';
        col.style.position = 'relative';
        const offsets = (isMobileSmall || targetColCount === 1) ? [0, 0] : [0, 200];
        col.dataset.colOffset = offsets[i % offsets.length];
        this.columns.push({
          el: col,
          offset: offsets[i % offsets.length],
          items: [],
          lastTransform: ''
        });
        fragment.appendChild(col);
      }

      this.items.forEach((item, index) => {
        const colIndex = index % targetColCount;
        this.columns[colIndex].el.appendChild(item);
        this.columns[colIndex].items.push({
          el: item,
          cachedOffset: 0,
          cachedHeight: 0,
          lastTransform: '',
          lastWillChange: ''
        });
      });

      this.grid.replaceChildren(fragment);
      this.currentColCount = targetColCount;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.columns.forEach((col) => this.resizeObserver.observe(col.el));
      }
    }

    calc() {
      this.winH = window.innerHeight;
      const isMobileSmall = typeof DeviceUtils !== 'undefined' && DeviceUtils.isMobileSmall;
      const hasTwoColumns = this.getAttribute('data-column') === '2';

      const targetColCount = (!hasTwoColumns || isMobileSmall) ? 1 : 2;
      if (this.currentColCount !== targetColCount) {
        this.rebuildGrid(targetColCount);
      }

      let contentHeightValue = '';
      if (this.parentSection) {
        const content = this.parentSection.querySelector('.media-vert-scroll__content');
        if (content) {
          const h = content.offsetHeight / 2;
          contentHeightValue = `${h.toFixed(2)}px`;
          this.contentElement = content;
        }
      }

      const style = window.getComputedStyle(this);
      const paddingBottom = parseFloat(style.paddingBottom) || 0;

      let maxTravelDistance = 0;
      this.columns.forEach((col) => {
        const colHeight = col.el.offsetHeight;
        const colOffset = parseFloat(col.offset) || 0;
        const startY = this.winH * 0.01 + colOffset;
        const endY = this.winH - colHeight - paddingBottom;

        col.computedStartY = startY;
        col.computedTravel = startY - endY;
        col.lastTransform = '';

        col.items.forEach(itemData => {
          itemData.cachedOffset = itemData.el.offsetTop;
          itemData.cachedHeight = itemData.el.offsetHeight;
          itemData.lastTransform = '';
          itemData.lastWillChange = '';
        });

        if (col.computedTravel > maxTravelDistance) maxTravelDistance = col.computedTravel;
      });

      const finalHeight = Math.max((maxTravelDistance * this.baseSpeed) + this.winH, this.winH * 2);
      const sectionHeightValue = `${finalHeight}px`;
      this.scrollDistance = finalHeight - this.winH;

      const rect = this.parentSection.getBoundingClientRect();
      this.sectionTop = rect.top + window.scrollY;

      if (contentHeightValue && contentHeightValue !== this.lastContentHeightValue) {
        this.contentElement.style.setProperty('--media-vert-scroll-content-height', contentHeightValue);
        this.lastContentHeightValue = contentHeightValue;
      }

      if (sectionHeightValue !== this.lastSectionHeightValue) {
        this.parentSection.style.height = sectionHeightValue;
        this.lastSectionHeightValue = sectionHeightValue;
      }

      this.updateElements(window.scrollY);
    }

    initObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isIntersecting = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.holdLazyImages();
            const rect = this.parentSection.getBoundingClientRect();
            this.sectionTop = rect.top + window.scrollY;

            const scroll = window.scrollY;
            let progress = (scroll - this.sectionTop) / this.scrollDistance;
            const rawTarget = Math.max(0, Math.min(1, progress));
            this.smoothedTarget = rawTarget;
            this.curr = rawTarget;
            this.target = rawTarget;

            this.updateElements(scroll);

            if (!this.raf) {
              this.animate();
            }
          } else if (this.raf) {
            cancelAnimationFrame(this.raf);
            this.raf = null;
          }
          if (!entry.isIntersecting) {
            if (entry.boundingClientRect.bottom <= 0) this.finishLazyHold();
            if (this.columns) this.releaseWillChange();
          }
        });
      }, { rootMargin: '10% 0px' });

      this.observer.observe(this.parentSection);
    }

    updateElements(scrollY) {
      const isMobileSmall = typeof DeviceUtils !== 'undefined' && DeviceUtils.isMobileSmall;
      const sectionRelativeTop = this.sectionTop - scrollY;

      this.columns.forEach((col) => {
        const colYPos = col.computedStartY - (this.curr * col.computedTravel);
        const colTransform = `translate3d(0, ${colYPos.toFixed(2)}px, 0)`;
        if (col.lastTransform !== colTransform) {
          col.el.style.transform = colTransform;
          col.lastTransform = colTransform;
        }

        const viewportColTop = sectionRelativeTop + colYPos;

        col.items.forEach((item) => {
          if (isMobileSmall && !this.mobileScaleEnabled) {
            if (item.lastTransform !== '') {
              item.el.style.transform = '';
              item.lastTransform = '';
            }
          } else {
            const itemVTop = viewportColTop + item.cachedOffset;
            const scaleStartPos = this.winH - item.cachedHeight;
            const scaleEndPos = -item.cachedHeight * 0.5;

            let progress = 0;
            if (item.cachedHeight < this.winH) {
              progress = (itemVTop - scaleStartPos) / (scaleEndPos - scaleStartPos);
            } else {
              const extendedRange = this.winH + (item.cachedHeight * 0.3);
              progress = (this.winH - itemVTop) / extendedRange;
            }

            progress = Math.max(0, Math.min(1, progress));
            const scale = 1 - (progress * this.scaleAmount);
            const itemTransform = `scale(${scale.toFixed(4)})`;
            if (item.lastTransform !== itemTransform) {
              item.el.style.transform = itemTransform;
              item.lastTransform = itemTransform;
            }

            if (item.lastWillChange !== 'transform') {
              item.el.style.willChange = 'transform';
              item.lastWillChange = 'transform';
            }
          }
        });
      });
    }

    releaseWillChange() {
      this.columns.forEach((col) => {
        col.items.forEach((item) => {
          if (item.lastWillChange !== 'auto') {
            item.el.style.willChange = 'auto';
            item.lastWillChange = 'auto';
          }
        });
      });
    }

    animate() {
      if (!this.isIntersecting) return;

      const currentScroll = window.scrollY;
      const sectionBottom = (this.sectionTop + this.scrollDistance + this.winH) - currentScroll;

      if (this.parentSection) {
        const isFixed = sectionBottom <= this.winH;
        if (this._wasFixed !== isFixed) {
          this.parentSection.classList.toggle('has-fixed-heading', isFixed);
          this._wasFixed = isFixed;
        }
      }

      let progress = (currentScroll - this.sectionTop) / this.scrollDistance;
      const rawTarget = Math.max(0, Math.min(1, progress));

      const targetDiff = rawTarget - this.smoothedTarget;
      this.smoothedTarget += targetDiff * 0.12;

      this.target = this.smoothedTarget;

      const diff = this.target - this.curr;
      this.curr += diff * this.lerpFactor;

      if (Math.abs(diff) < 0.0001) {
        this.curr = this.target;
      }

      this.updateElements(currentScroll);
      this.raf = requestAnimationFrame(() => this.animate());
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.removeEventListener('load', this.boundOnLoad);
      window.removeEventListener('resize', this.boundOnResize);
      if (this.calcRaf) cancelAnimationFrame(this.calcRaf);
      this.releaseLazyImages();
    }
  });
}